export type AccommodationsDefaultLocation = Readonly<{
  lat: number;
  long: number;
  name: string;
  placeId: string;
}>;

/**
 * Default location used for accommodations experiences when an event does not have a usable location anchor.
 */
export const ACCOMMODATIONS_DEFAULT_LOCATION: AccommodationsDefaultLocation = {
  lat: 40.7127753,
  long: -74.0059728,
  name: 'New York, NY, USA',
  placeId: 'ChIJOwg_06VPwokRYv534QaPC8g',
};

export const HotelSearchSource = {
  OTA: 'ota',
  GUEST_MAP: 'guest_map',
  ADMIN_MAP: 'admin_map',
} as const;

export type HotelSearchSource = (typeof HotelSearchSource)[keyof typeof HotelSearchSource];

// Sources where the CTA should still appear for sold-out hotels (e.g., to let couples add them to their event)
export const SOLD_OUT_CTA_ALLOWED_SOURCES: ReadonlyArray<HotelSearchSource> = [HotelSearchSource.ADMIN_MAP];

/**
 * Which fetch path produced the price a guest saw.
 *
 * This is a closed type, not a free string. It names one of two code paths. It does not grow with
 * the number of pages the product has. A third value would mean a third fetch path exists, and that
 * must fail at the type system rather than appear quietly in a chart.
 *
 * `SNAPSHOT` names the `travel_rate_snapshot` table the value describes. A snapshot holds one row
 * per joyPlace, regardless of provider. So a snapshot price is a sole-bidder result by construction.
 * The guest can click it and land on a live price from a different provider.
 */
export const PriceSource = {
  SNAPSHOT: 'snapshot',
  LIVE: 'live',
} as const;

export type PriceSource = (typeof PriceSource)[keyof typeof PriceSource];

/**
 * Map layout mode for the accommodations console.
 *
 * - `full`: Map is always visible and takes up a portion of the screen alongside listings.
 *   Use this for desktop views where you want the map and listings side-by-side.
 *
 * - `compact`: Map is hidden by default and can be expanded by the user.
 *   Use this for space-constrained layouts or when you want to prioritize listings visibility.
 *
 * - `mapOnly`: Only the map is shown — no header, filters panel, or listings panel.
 *   Use this for embedded map views (e.g., the admin preview explore area).
 */
export const HotelSearchMapLayout = {
  FULL: 'full',
  COMPACT: 'compact',
  MAP_ONLY: 'mapOnly',
} as const;

export type HotelSearchMapLayout = (typeof HotelSearchMapLayout)[keyof typeof HotelSearchMapLayout];

export const HotelSearchContextParams = {
  source: 'source',
  layout: 'layout',
  selectedHotel: 'selectedHotel',
} as const;

export type HotelSearchContextParams = (typeof HotelSearchContextParams)[keyof typeof HotelSearchContextParams];

export const SortOption = {
  COUPLES_PICKS: 'couple-picks',
  TOP_MATCHES: 'top-matches',
  PRICE_LOW_TO_HIGH: 'price-low-to-high',
  PRICE_HIGH_TO_LOW: 'price-high-to-low',
  MOST_SAVINGS: 'most-savings',
  HIGHEST_DISCOUNT: 'highest-discount',
  DISTANCE: 'distance',
  // Cash-fund variant only — groups the stays the fund applies to at the top (TRAV-3348).
  HIGHEST_FUND_SAVINGS: 'highest-fund-savings',
} as const;

export type SortOption = (typeof SortOption)[keyof typeof SortOption];
